import { skipToken, useQuery } from "@apollo/client/react";
import { Box, Collapse, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/system";
import { Cross, SearchInside } from "@pmag/icons";
// eslint-disable-next-line no-restricted-imports
import { IconButton, InlineError, SearchBox, Tab, Tabs, useLocalStorage, useSessionStorage } from "@pmag/ui-components";
import { type GQLComponentGroupOverviewModelQuery, type GQLComponentGroupOverviewModelQueryVariables } from "@src/graphql.generated";
import { useGlobalState } from "@src/hooks/useGlobalState";
import { componentGroupOverviewModelQuery } from "@src/pages/componentGroupOverview/ComponentGroupOverviewPage.gql";
import { BoxContainer, ControlPanel, StyledSearchBox, TabContainer } from "@src/pages/componentGroupOverview/ComponentGroupOverviewPage.styles";
import { ComponentGroupOverviewCategoryTabs } from "@src/pages/componentGroupOverview/ComponentGroupOverviewPage.utils";
import { ComponentGroupOverviewPageSkeleton } from "@src/pages/componentGroupOverview/ComponentGroupOverviewPageSkeleton";
import { ArticleHeader } from "@src/pages/componentGroupOverview/components/articleHeader/ArticleHeader";
import { EngineTab } from "@src/pages/componentGroupOverview/components/engineTab/EngineTab";
import { FrameTab } from "@src/pages/componentGroupOverview/components/frameTab/FrameTab";
import { WPPartsTab } from "@src/pages/componentGroupOverview/components/wpPartsTab/WPPartsTab";
import { type FunctionComponent, useEffect, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as yup from "yup";

export const dataTestId = {
    pageContainer: "sparePartsFinder.componentGroupOverviewPage.pageContainer",
    loadingSkeleton: "sparePartsFinder.componentGroupOverview.loadingSkeleton",
    inlineError: "sparePartsFinder.componentGroupOverview.InlineError",

    tabsCategory: "sparePartsFinder.componentGroupOverview.tabsCategory",
    tabCategoryFrame: "sparePartsFinder.componentGroupOverview.tabCategoryFrame",
    tabCategoryEngine: "sparePartsFinder.componentGroupOverview.tabCategoryEngine",
    tabCategoryWPParts: "sparePartsFinder.componentGroupOverview.tabCategoryWPParts",

    searchBox: "sparePartsFinder.componentGroupOverview.searchBox",
    buttonClearSearch: "sparePartsFinder.componentGroupOverview.buttonClearSearch",
};

export const categoryTabSchema = yup.string().oneOf(Object.values(ComponentGroupOverviewCategoryTabs)).defined();

export const ComponentGroupOverviewPage: FunctionComponent = ({ ...restProps }) => {
    const intl = useIntl();
    const theme = useTheme();
    const { modelId } = useGlobalState();

    const [searchTerm, setSearchTerm] = useState("");
    const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
    const [isSearchBarVisible, setSearchBarVisible] = useState(false);

    const [category, setCategory] = useSessionStorage<ComponentGroupOverviewCategoryTabs>(
        "sparePartsFinderOverviewCategoryTab",
        ComponentGroupOverviewCategoryTabs.Frame,
        categoryTabSchema,
    );

    const [viewedModels, setViewedModels] = useLocalStorage<string[]>("recentlyViewedModels", [], yup.array().of(yup.string().required()).required());
    const [isInitialRender, setIsInitialRender] = useState(true);

    const { data, error, loading, refetch } = useQuery<GQLComponentGroupOverviewModelQuery, GQLComponentGroupOverviewModelQueryVariables>(
        componentGroupOverviewModelQuery,
        modelId ? { variables: { id: modelId, languageCode: intl.locale === "de" ? "de" : "en" } } : skipToken,
    );

    const model = data?.model;

    useEffect(() => {
        // viewedModels is empty first render
        if (isInitialRender) {
            setIsInitialRender(false);

            return;
        }
        if (data && modelId && !viewedModels.includes(data.model.id)) {
            const updatedList = [modelId, ...viewedModels];
            setViewedModels(updatedList);
        }
    }, [modelId, setViewedModels, viewedModels, isInitialRender, data]);

    if (loading) return <ComponentGroupOverviewPageSkeleton data-testid={dataTestId.loadingSkeleton} />;

    if (error || !modelId || !model) {
        return <InlineError onRetry={refetch} data-testid={dataTestId.inlineError} />;
    }

    // when a model has no engineArticleNumber, we should not show tabs, because in Engine and WPParts tab there would not be any result. therefore we use Frame tab by default
    // if model isWPArticle only Frame tab is visible
    const tabsValue = model?.engineArticleNumber && !model?.isWpArticle ? category : ComponentGroupOverviewCategoryTabs.Frame;

    return (
        <BoxContainer data-testid={dataTestId.pageContainer} {...restProps}>
            <ArticleHeader model={model} />

            <ControlPanel>
                {!model.isWpArticle && model?.engineArticleNumber && (
                    <Box>
                        <Tabs
                            value={tabsValue}
                            onChange={(_, value) => setCategory(value)}
                            color="defaultBackground"
                            size="small"
                            variant="contentSwitcher"
                            data-testid={dataTestId.tabsCategory}
                        >
                            <Tab
                                value={ComponentGroupOverviewCategoryTabs.Frame}
                                label={<FormattedMessage id="sparePartsFinder.componentGroupOverview.tabs.frame" defaultMessage="Frame" />}
                                data-testid={dataTestId.tabCategoryFrame}
                            />

                            <Tab
                                value={ComponentGroupOverviewCategoryTabs.Engine}
                                label={<FormattedMessage id="sparePartsFinder.componentGroupOverview.tabs.engine" defaultMessage="Engine" />}
                                data-testid={dataTestId.tabCategoryEngine}
                            />

                            <Tab
                                value={ComponentGroupOverviewCategoryTabs.WPParts}
                                label={<FormattedMessage id="sparePartsFinder.componentGroupOverview.tabs.wpParts" defaultMessage="WP Parts" />}
                                data-testid={dataTestId.tabCategoryWPParts}
                            />
                        </Tabs>
                    </Box>
                )}

                {!isSmallScreen ? (
                    <SearchBox
                        placeholder={intl.formatMessage({
                            id: "sparePartsFinder.componentGroupOverview.searchBox.placeholder",
                            defaultMessage: "Search ...",
                        })}
                        fullWidth
                        size="small"
                        onChange={(event) => setSearchTerm(event.target.value)}
                        value={searchTerm}
                        endAdornment={
                            searchTerm !== "" && (
                                <IconButton variant="transparent" onClick={() => setSearchTerm("")} data-testid={dataTestId.buttonClearSearch}>
                                    <Cross />
                                </IconButton>
                            )
                        }
                        data-testid={dataTestId.searchBox}
                    />
                ) : (
                    <IconButton
                        variant="secondary"
                        onClick={() => {
                            if (isSearchBarVisible) setSearchTerm("");
                            setSearchBarVisible(!isSearchBarVisible);
                        }}
                    >
                        {isSearchBarVisible ? <Cross size="small" /> : <SearchInside size="small" />}
                    </IconButton>
                )}
            </ControlPanel>

            {isSmallScreen && (
                <Collapse in={isSearchBarVisible}>
                    <StyledSearchBox
                        placeholder={intl.formatMessage({
                            id: "sparePartsFinder.componentGroupOverview.searchBox.placeholder",
                            defaultMessage: "Search ...",
                        })}
                        fullWidth
                        size="small"
                        onChange={(event) => setSearchTerm(event.target.value)}
                        value={searchTerm}
                        endAdornment={
                            searchTerm !== "" && (
                                <IconButton variant="transparent" onClick={() => setSearchTerm("")} data-testid={dataTestId.buttonClearSearch}>
                                    <Cross />
                                </IconButton>
                            )
                        }
                        data-testid={dataTestId.searchBox}
                    />
                </Collapse>
            )}

            <TabContainer>
                {tabsValue === ComponentGroupOverviewCategoryTabs.Frame && (
                    <FrameTab
                        searchTerm={searchTerm}
                        data={
                            model?.frameComponentGroupParents.slice().sort((a, b) => a.componentGroup.code.localeCompare(b.componentGroup.code)) ?? []
                        }
                        loading={loading}
                        brandColor={model.brand?.color}
                    />
                )}

                {tabsValue === ComponentGroupOverviewCategoryTabs.Engine && (
                    <EngineTab
                        searchTerm={searchTerm}
                        data={
                            model?.engineComponentGroupParents.slice().sort((a, b) => a.componentGroup.code.localeCompare(b.componentGroup.code)) ??
                            []
                        }
                        loading={loading}
                        brandColor={model.brand?.color}
                    />
                )}

                {tabsValue === ComponentGroupOverviewCategoryTabs.WPParts && (
                    <WPPartsTab searchTerm={searchTerm} data={model?.wpArticles ?? []} loading={loading} />
                )}
            </TabContainer>
        </BoxContainer>
    );
};
